import type { RouteLocationRaw } from 'vue-router';
import type { AnyItem, CompositePrimaryKey, CreateModeItemId, Filter, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, MonospacePage, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';

export type ListingPageSelectionMode = 'single' | 'multiple';

export const DetailPageDismissAction = {
	Back: 'back',
	Close: 'close',
} as const;

export type DetailPageDismissAction = typeof DetailPageDismissAction[keyof typeof DetailPageDismissAction];

export interface ListingPageDefaultViewOptions {
	displayOptions?: Record<string, unknown>;
	filter?: Filter;
	layoutId: string;
	name?: string;
	icon?: string;
}

export interface PageRootContext {
	/**
	 * Current page ID.
	 */
	pageId?: MonospacePage['id'] | null;
	/**
	 * Current page type
	 */
	pageType?: MonospacePage['type'] | null;
	/**
	 * Current page intent
	 */
	pageIntent?: MonospacePage['intent'] | null;
	/**
	 * Is current page full width
	 */
	pageIsFullWidth?: boolean | null;
	/**
	 * Display mode of the current page.
	 */
	pageDisplayMode?: 'drawer' | 'modal';
}

export interface SpaceRootContext {
	/**
	 * Is the space empty
	 */
	spaceIsEmpty?: boolean;
}

interface CommonPageContext extends PageRootContext {
	/**
	 * Default collection ID
	 */
	collectionId?: MonospacePage['collectionId'] | null;
}

export interface FieldOverrideOptions {
	disabled?: boolean;
	hidden?: boolean;
}

export interface DetailPageContext extends CommonPageContext {
	/**
	 * Default item being edited.
	 */
	itemId?: CreateModeItemId | CompositePrimaryKey;
	/**
	 * If the user needs to determine whether a form should be submitted or not.
	 */
	formMode?: 'submit' | 'emit';
	/**
	 * Item payload if we don't want to pass an itemId (e.g. if the item hasn't been created yet)
	 */
	itemEdits?: AnyItem;
	/**
	 * Field overrides for specific fields (e.g. to hide recursive relation fields).
	 */
	fieldOverrides?: Record<GlobalSingleRelationFieldId | GlobalPrimitiveFieldId, FieldOverrideOptions>;
	/**
	 * Override the route the header dismisses to, whichever `dismissAction` is used
	 * (defaults to collection listing route).
	 */
	backRoute?: RouteLocationRaw;
	/**
	 * Whether the page offers the delete action, on top of the delete entitlement.
	 *
	 * Set this to `false` for items the backend refuses to delete as a business rule rather than
	 * through entitlements, such as the workspace's public role. Defaults to allowed.
	 */
	allowDelete?: boolean;
	/**
	 * How the header offers to leave the item (defaults to `Back`).
	 */
	dismissAction?: DetailPageDismissAction;
}

export interface ListingPageContext extends CommonPageContext {
	/**
	 * In case the user needs to select items, what selection mode to use by default.
	 */
	selectionMode?: ListingPageSelectionMode;
	/**
	 * Currently selected items.
	 */
	selection?: CompositePrimaryKey[];
	/**
	 * Unique fields used to identify selected records. Defaults to the collection's primary key.
	 */
	selectionKeys?: ResolvedMonospacePrimitiveField[];
	/**
	 * Additional key fields included in selection snapshots alongside the selection keys.
	 * Prefilled selections must already contain these values; selection state does not fetch them.
	 */
	projectionKeys?: ResolvedMonospacePrimitiveField[];
	/**
	 * Items to be excluded from the available items.
	 */
	filter?: Filter;
	/**
	 * Default view options for the listing.
	 */
	defaultView?: ListingPageDefaultViewOptions;
	/**
	 * Prefix for persistence storage keys to disambiguate listings of the same collection.
	 */
	persistenceKeyPrefix?: string;
}
